import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../services/supabaseClient';
import './Auth.css';
import { initializeCheckout } from '../../services/stripe/stripeService';

const AccountStatusPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const type = params.get('type'); // 'past_due' or 'inactive'

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleAction = async () => {
    setLoading(true);
    setMessage('');

    try {
      // ✅ Get currently authenticated user
      const { data: authData, error: authError } = await supabase.auth.getUser();
      if (authError || !authData?.user) {
        setMessage('User not authenticated.');
        setLoading(false);
        return;
      }

      const auth_id = authData.user.id;

      // ✅ Fetch stripe_customer_id (and subscription_id for reference)
      const { data, error } = await supabase
        .from('users')
        .select('stripe_customer_id, stripe_subscription_id')
        .eq('auth_id', auth_id)
        .single();

      if (error || !data?.stripe_customer_id) {
        console.error('❌ Stripe customer ID not found:', error);
        setMessage('Unable to find billing information for this account.');
        setLoading(false);
        return;
      }

      // ---- Handle Past Due (Billing Portal) ----
      if (type === 'past_due') {
        const res = await fetch('https://askrexi.comverex.ai/create-portal-session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customer_id: data.stripe_customer_id }),
        });
        const json = await res.json();

        if (json?.url) {
          window.open(json.url, '_blank', 'noopener,noreferrer');
        } else {
          console.error('❌ Portal creation failed:', json);
          setMessage('Unable to open billing portal.');
        }
      }

      // ---- Handle Inactive (Checkout Reactivation) ----
      if (type === 'inactive') {
        // 🧩 Use the same helper used in AccountSetup for consistent Stripe handling
        console.log('🔁 Starting reactivation checkout flow...');
        await initializeCheckout(''); // Server reads prices from .env
      }

    } catch (err) {
      console.error('💥 Stripe navigation error:', err);
      setMessage('Something went wrong connecting to Stripe.');
    } finally {
      setLoading(false);
    }
  };

  // ✅ After Stripe redirect (e.g., success_url or cancel_url), detect query params
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusParam = params.get('status');
    if (statusParam === 'success') {
      // Redirect to Test Reports after successful payment
      navigate('/dashboard/testreports');
    }
  }, [location.search, navigate]);

  return (
    <div className="auth-container">
      <div className="auth-card">
        {type === 'past_due' ? (
          <>
            <h2>Your Account is Past Due</h2>
            <p style={{ textAlign: 'center', marginBottom: 30 }}>
              Your payment method could not be processed.<br />
              No worries, update your card to regain access.
            </p>
            {message && <div className="error-message">{message}</div>}
            <button className="auth-button" onClick={handleAction} disabled={loading}>
              {loading ? 'Connecting...' : 'Update Payment Method'}
            </button>
          </>
        ) : (
          <>
            <h2>Reactivate Your Account</h2>

            <p style={{ textAlign: 'center', marginBottom: 30 }}>
              Clicking <strong>Reactivate</strong> will bring you to the checkout page and confirm you have read and agreed to our{' '}
              <a
                href="https://www.comverex.com/terms_and_conditions.php"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-link"
              >
                Terms & Conditions
              </a>{' '}
              and{' '}
              <a
                href="https://www.comverex.com/privacy_policy.php"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-link"
              >
                Privacy Policy
              </a>.
            </p>

            <p style={{ textAlign: 'center' }}>
              Your Comverex subscription is <strong>$95/month</strong>, and each test report you upload for
              analysis is charged a <strong>$2 processing fee</strong>.
            </p>

            <p style={{ textAlign: 'center', marginBottom: 30 }}>
              You can cancel your subscription at any time.
            </p>

            {message && <div className="error-message">{message}</div>}

            <button className="auth-button" onClick={handleAction} disabled={loading}>
              {loading ? 'Connecting...' : 'Reactivate Subscription'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AccountStatusPage;
